import { ConfigProvider as AntdConfigProvider, App } from '@yimoka/antd';
import enUS from '@yimoka/antd/locale/en_US';
import zhCN from '@yimoka/antd/locale/zh_CN';
import { MiniRoute } from '@yimoka/mini-router';
import { observer } from '@yimoka/react';
import * as ReactModule from 'react';
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { ModuleUpdatePrompt } from '@/components/module-update-prompt';
import { PwaUpdatePrompt } from '@/components/pwa-update-prompt';
import { TenantWebConfig } from '@/config/app-config';
import { getEnv } from '@/env';
import { SsoLayout } from '@/layouts/sso';
import { I18nRoot } from '@/locales/i18n';
import { getCurrentLanguage, subscribeLanguage } from '@/locales/language';
import { initRemotes } from '@/mf/init-remotes';
import type { ModuleVersionMap } from '@/mf/types';
import { fetchModuleVersionMap } from '@/mf/version-map';
import {
  AccountLoginPage,
  OIDCConsentPage,
  OIDCErrorPage,
  OIDCLoginPage,
  OIDCLogoutPage,
  OIDCLogoutSuccessfulPage,
  OAuth2CallbackPage,
  TenantJoinAddPage,
  TenantJoinPage,
  TenantSelectorHeaderAction,
  TenantSelectorPage,
} from '@/pages/account';
import { TenantEntry } from '@/pages/account/tenant-entry';
import OfficialHomePage from '@/pages/official/home';
import { RealmProfileContext } from '@/realm-profile/context';
import { useRealmDocumentMetadata } from '@/realm-profile/document-metadata';
import { createTenantWebRealmTheme } from '@/realm-profile/profile';
import { getRealmProfileState, loadRealmProfile } from '@/realm-profile/store';
import { setRuntimeReact } from '@/runtime/window-globals';

/** AppContent 初始化真实业务 remotes，并只注册 Host 自己拥有的认证、官网与租户入口。 */
function AppContent() {
  const [versionMap, setVersionMap] = useState<ModuleVersionMap>();

  useEffect(() => {
    setRuntimeReact(ReactModule);
    void fetchModuleVersionMap(getEnv().moduleVersionsUrl, { tenantID: getEnv().tenantID })
      .then((map) => {
        initRemotes(map);
        setVersionMap(map);
      });
  }, []);

  return (
    <>
      <PwaUpdatePrompt />
      {versionMap && <ModuleUpdatePrompt versionMap={versionMap} />}
      <MiniRoute component={<SsoLayout><AccountLoginPage /></SsoLayout>} path="/login" />
      <MiniRoute component={<SsoLayout><OAuth2CallbackPage /></SsoLayout>} path="/oauth2/callback" />
      <MiniRoute component={<SsoLayout><OIDCLoginPage /></SsoLayout>} path="/oauth" />
      <MiniRoute component={<SsoLayout><OIDCLoginPage /></SsoLayout>} path="/oauth/login" />
      <MiniRoute component={<SsoLayout><OIDCConsentPage /></SsoLayout>} path="/oauth/consent" />
      <MiniRoute component={<SsoLayout><OIDCErrorPage /></SsoLayout>} path="/oauth/error" />
      <MiniRoute component={<SsoLayout><OIDCLogoutPage /></SsoLayout>} path="/oauth/logout" />
      <MiniRoute
        component={<SsoLayout><OIDCLogoutSuccessfulPage /></SsoLayout>}
        path="/oauth/logout-successful"
      />
      <MiniRoute
        component={(
          <SsoLayout headerExtra={<TenantSelectorHeaderAction />}>
            <TenantSelectorPage />
          </SsoLayout>
        )}
        path="/tenant-selector"
      />
      <MiniRoute component={<SsoLayout><TenantJoinPage /></SsoLayout>} path="/tenant/join" />
      <MiniRoute component={<SsoLayout><TenantJoinAddPage /></SsoLayout>} path="/tenant/join/add" />
      <MiniRoute component={<TenantEntry remotesReady={Boolean(versionMap)} />} path="/t/*" />
      <MiniRoute
        component={(
          <BrowserRouter>
            <Routes>
              <Route element={<OfficialHomePage />} path="/" />
              <Route element={<Navigate replace to="/" />} path="*" />
            </Routes>
          </BrowserRouter>
        )}
        path="*"
      />
    </>
  );
}

/** AppShell 提供全站 UI、运行配置与语言 Provider，业务布局由租户入口按路由挂载。 */
export const AppShell = observer(() => {
  const language = useSyncExternalStore(subscribeLanguage, getCurrentLanguage, getCurrentLanguage);
  const realmName = getEnv().realm;
  const realmState = getRealmProfileState(realmName);

  // 渠道标题由 Portal 按当前语言投影，切换语言必须重新读取，不能继续复用旧 locale 快照。
  useEffect(() => {
    void loadRealmProfile(realmName);
  }, [language, realmName]);

  /** retry 强制刷新当前渠道的远端配置。 */
  const retry = useCallback(() => {
    void loadRealmProfile(realmName, true);
  }, [realmName]);
  const realmContext = useMemo(() => ({
    loading: realmState.loading,
    profile: realmState.profile,
    retry,
    source: realmState.source,
  }), [realmState.loading, realmState.profile, realmState.source, retry]);
  useRealmDocumentMetadata(realmState.profile);

  return (
    <RealmProfileContext.Provider value={realmContext}>
      <AntdConfigProvider locale={language === 'zh-CN' ? zhCN : enUS} theme={createTenantWebRealmTheme(realmState.profile)}>
        <App>
          <TenantWebConfig>
            <I18nRoot>
              <AppContent />
            </I18nRoot>
          </TenantWebConfig>
        </App>
      </AntdConfigProvider>
    </RealmProfileContext.Provider>
  );
});
